// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to fetch location data from the API.
 * Sorting, searching, and filtering are performed server-side.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CrmSyncStatus } from '@/constants/crm-sync-status';
import { Sort } from '@/constants/sort';
import { locationSchema } from '@/location/api/types';
import queryString from 'query-string';
import { z } from 'zod';

/**
 * Valid properties for sorting locations server-side.
 */
type LocationSortProperty = 'name' | 'company' | 'assetCount';

/**
 * Schema for validating the API response when fetching locations.
 * Transforms the envelope response into a flat { locations, totalCount } shape.
 */
const locationsResponseSchema = z
  .object({
    data: z
      .object({
        results: z.array(locationSchema),
        totalCount: z.number(),
      })
      .nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    locations: data?.results ?? [],
    totalCount: data?.totalCount ?? 0,
  }));

type LocationsResponse = z.infer<typeof locationsResponseSchema>;

/**
 * Interface defining the available filters for location queries.
 * Pagination and sort fields are optional — callers that omit them receive
 * all locations (the backend defaults to a large page size).
 */
export interface LocationFilter {
  isArchived?: boolean | 'all';
  isTransient?: boolean | 'all';
  /** Kept for backward compat with customer-sites-stats-card */
  customerId?: string;
  /** Array of customer UUIDs; may include the sentinel 'ORGANIZATION' */
  customerIds?: string[];
  query?: string;
  syncStatuses?: CrmSyncStatus[];
  // Pagination / sort — only sent by the listing table
  page?: number;
  pageSize?: number;
  sort?: Sort<LocationSortProperty>;
}

/**
 * Maps a frontend sort direction to the backend sortOrder param.
 */
function toSortOrder(
  sort: Sort<LocationSortProperty> | undefined
): string | undefined {
  if (!sort) {
    return undefined;
  }
  return sort.direction === 'desc' ? 'DESC' : 'ASC';
}

/**
 * Maps a frontend sort property to the backend sortBy param.
 */
function toSortBy(
  sort: Sort<LocationSortProperty> | undefined
): string | undefined {
  if (!sort) {
    return undefined;
  }
  const map: Record<LocationSortProperty, string> = {
    name: 'name',
    company: 'company',
    assetCount: 'asset_count',
  };
  return map[sort.property];
}

/**
 * Fetches locations from the API with optional server-side filtering, sorting,
 * and pagination.
 *
 * Callers that omit page/pageSize receive all locations (backend default
 * page size is 5000).
 */
export async function getLocations(
  filter?: LocationFilter
): Promise<LocationsResponse> {
  const path = '/v1/location';

  // Build forCustomerIds: keep 'ORGANIZATION' sentinel in the array so the
  // backend's FromQueryParams can detect it. Fall back to legacy single-customer
  // filter if the new customerIds field is not provided.
  const legacyCustomerId = filter?.customerId;
  const forCustomerIds =
    filter?.customerIds && filter.customerIds.length > 0
      ? filter.customerIds
      : legacyCustomerId
        ? [legacyCustomerId]
        : undefined;

  const response = await api.get(path, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      archived: filter?.isArchived,
      isTransient: filter?.isTransient,
      forCustomerIds: forCustomerIds,
      search: filter?.query || undefined,
      syncStatuses: filter?.syncStatuses,
      page: filter?.page,
      limit: filter?.pageSize,
      sortBy: toSortBy(filter?.sort),
      sortOrder: toSortOrder(filter?.sort),
    },
  });

  return parseResponse(locationsResponseSchema, response.data, {
    schemaName: 'Locations',
    endpoint: path,
  });
}
